import { POLLS_API_PREFIX } from '@/config/polls'

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/max/polls/lab-membership → { isMember, checkedAt, cacheTtlSec, ... }
// ТЗ v3.1a §4.5 + v3.1b-1 rename + backend contract 2026-07-23 от B.
//
// Auth: cookie auth_token (ЛК) — X-Max-Init-Data не нужен для ЛК-веба (обяз.
// только для миниаппа B). Header X-Lab-Recheck: true — bypass Redis-кэша
// на бэке, дёргает MAX API живьём (для refetch после клика «Подписаться и
// продолжить» и в 30s-polling fallback R2 внутри MAX webview).
//
// Три shape'а 200-ответа:
// - subscribed:      { isMember: true,  checkedAt, cacheTtlSec: 300 }
// - not subscribed:  { isMember: false, checkedAt, cacheTtlSec: 300,
//                      labChannelDeepLink, chatName: 'Telegraphyx Lab' }
// - fail-open:       { isMember: true,  checkFailed: true, cacheTtlSec: 60 }
//                    (MAX API таймаут/5xx — пускаем чтоб не блокировать всех;
//                     TTL 60с чтобы false-positive шлейф не тянулся 5 мин).
// ─────────────────────────────────────────────────────────────────────────────

export interface LabMembership {
  isMember: boolean
  /** ISO-timestamp когда бэк последний раз реально дёргал MAX API. */
  checkedAt?: string
  /** Сколько секунд бэк рекомендует держать этот ответ в кэше. 300 happy / 60 fail-open. */
  cacheTtlSec?: number
  /** Deep-link на канал Telegraphyx Lab — присутствует только в !isMember ответах. */
  labChannelDeepLink?: string
  /** Имя канала для копий попапа — присутствует только в !isMember ответах. */
  chatName?: string
  /** true если бэк отдал fail-open (MAX API upstream был недоступен). */
  checkFailed?: boolean
}

// DEP-1a (2026-07-23 15:42 MSK): backend /lab-membership живой на prod
// (B contract 2026-07-23). Переключаем на реальный fetch. src/api/polls.ts
// (CRUD list/create/publish/detail/timeseries/voters/close/duplicate) пока
// на моках — backend CRUD ещё не готов.
const USE_MOCKS = false

// ─────────────────────────────────────────────────────────────────────────────
// Реальный fetch — с двойной защитой (.catch → null, fail-open на любую сеть).
// ─────────────────────────────────────────────────────────────────────────────

async function fetchLabMembershipReal(params: { bypassCache: boolean }): Promise<LabMembership | null> {
  try {
    const r = await fetch(`${POLLS_API_PREFIX}/lab-membership`, {
      credentials: 'include',
      headers: params.bypassCache ? { 'X-Lab-Recheck': 'true' } : undefined,
    })
    if (!r.ok) return null
    return (await r.json()) as LabMembership
  } catch {
    return null
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock: Виктор (8931841) = член (bypass superadmin ID в env).
// В Phase 1 фронт-моке возвращаем всегда isMember=true — до sync 28.07
// проверять негативный сценарий через ручное форсирование ниже.
// ─────────────────────────────────────────────────────────────────────────────

const MOCK_DEEP_LINK = 'https://max.ru/join/QPw5mlvvnacOSpr3llA6QkqvooFCPlHGqA6AfK15eXk'

function fetchLabMembershipMock(): LabMembership {
  // dev-override: `localStorage.setItem('tgx-mock-lab-not-member', '1')` в
  // devtools заставит mock вернуть !isMember. Плюс:
  // `localStorage.setItem('tgx-mock-lab-fail-open', '1')` — вернёт fail-open
  // shape (checkFailed=true, TTL=60) для проверки того что useLabMembership
  // эмитит poll_lab_check_failed на explicit backend fail-open (не только
  // на .catch → null).
  const nowIso = new Date().toISOString()
  if (typeof window !== 'undefined' && localStorage.getItem('tgx-mock-lab-fail-open') === '1') {
    return { isMember: true, checkFailed: true, cacheTtlSec: 60 }
  }
  const forceNotMember = typeof window !== 'undefined' && localStorage.getItem('tgx-mock-lab-not-member') === '1'
  if (forceNotMember) {
    return {
      isMember: false,
      checkedAt: nowIso,
      cacheTtlSec: 300,
      labChannelDeepLink: MOCK_DEEP_LINK,
      chatName: 'Telegraphyx Lab',
    }
  }
  return {
    isMember: true,
    checkedAt: nowIso,
    cacheTtlSec: 300,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchLabMembership(params: { bypassCache: boolean }): Promise<LabMembership | null> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 80))
    return fetchLabMembershipMock()
  }
  return fetchLabMembershipReal(params)
}
